import { CodeEditorService } from './code-editor-service'
import { type CodeContext } from '@lg/shared/for-studio'
import { DeclarationService } from '../../services/declaration.service'
import type * as Monaco from 'monaco-editor'
import {
	Component,
	forwardRef,
	OnDestroy,
	ChangeDetectionStrategy,
	ElementRef,
	HostListener,
	inject,
	input,
	output,
	model,
	effect,
	computed,
	signal,
	viewChild,
} from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { type TsCodeType, type CodeEditorSize } from '@lg/shared'
import { setUpWrappers } from './code-editor-helpers'
import { type CodeLanguageType } from '@lg/shared'
import { DevService } from '../../services/dev-service.js'
declare const monaco: typeof Monaco
// import * as editorWorker from 'monaco-editor/esm/vs/editor/editor.worker.js'
// import * as jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker.js'
// import * as cssWorker from 'monaco-editor/esm/vs/language/css/css.worker.js'
// import * as htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker.js'
// import * as tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker.js'

const languageAliases: Record<CodeLanguageType, string> = {
	ts: 'typescript',
	html: 'html',
	scss: 'scss',
	sql: 'sql',
	json: 'json',
	yaml: 'yaml',
}

const DontProtectWrappers = false

// self.MonacoEnvironment = {
// 	async getWorker(workerId, label) {
// 		console.log('getWorker', workerId, label)

// 		switch (label) {
// 			case 'json':
// 				return jsonWorker.initialize()
// 			case 'css':
// 				return cssWorker.initialize()
// 			case 'html':
// 				return htmlWorker.initialize()
// 			case 'typescript':
// 				return tsWorker.initialize()
// 			default:
// 				return editorWorker.initialize()
// 		}
// 	},
// }

@Component({
	selector: 'code-editor-new',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CodeEditorComponent),
			multi: true,
		},
	],
	templateUrl: './code-editor.component.html',
	styleUrls: ['./code-editor.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CodeEditorComponent implements OnDestroy, ControlValueAccessor {
	private static staticInitPromise: Promise<void> | undefined
	private readonly devService = inject(DevService)

	wrapperRef = viewChild<ElementRef<HTMLDivElement>>('wrapper')
	private wrapperWidth = signal(0)

	code = model<string>('')
	codeReadFromEditor = signal<string | undefined>(undefined)
	size = input<CodeEditorSize>('normal')
	/** @deprecated try to remove this and centeralize the type declarations */
	typeDeclarations = input<string>('')
	context = input<CodeContext | undefined>(undefined)
	contextKnownToEditor = signal<CodeContext | undefined>(undefined)
	blur = output<void>()
	selectionsChange = output<string[]>()

	cssClasses = computed(() => {
		const context = this.context()
		return [
			`code-editor--${this.size()}`,
			context?.theme == 'vs' ? 'code-editor--invert' : '',
		]
	})

	static counter = 0

	/** @deprecated */
	onChange = (_value: TsCodeType) => {}
	/** @deprecated */
	onTouched = () => {}
	/** @deprecated */
	disabled = false

	private document = inject(DOCUMENT)
	isFullscreen = signal(false)
	private originalParent: HTMLElement | null = null
	private originalNextSibling: Node | null = null

	@HostListener('document:keydown.escape')
	onEscape() {
		if (this.isFullscreen()) {
			this.closeFullscreen()
		}
	}

	openFullscreen() {
		const el = this.wrapperRef()?.nativeElement

		if (!el) {
			return
		}

		this.wrapperWidth.set(el.offsetWidth)
		this.originalParent = el.parentElement
		this.originalNextSibling = el.nextSibling

		el.style.width = ''
		this.document.body.appendChild(el)
		this.isFullscreen.set(true)
	}

	closeFullscreen() {
		const el = this.wrapperRef()?.nativeElement

		if (!el) {
			return
		}

		if (this.originalParent) {
			this.originalParent.insertBefore(el, this.originalNextSibling)
			this.originalParent = null
			this.originalNextSibling = null
		}

		const width = this.wrapperWidth()
		if (width) {
			el.style.width = `${width}px`
		}

		this.isFullscreen.set(false)
	}

	constructor(
		private declarationService: DeclarationService,
		private editorService: CodeEditorService,
	) {
		editorService.load()

		const initEffect = effect(() => {
			const context = this.context()
			const isLoaded = this.editorService.isLoaded()
			if (!context || !isLoaded) return

			initEffect.destroy()
			this.initMonaco()
		})

		effect(() => {
			const isLoaded = this.editorService.isLoaded()
			const typeDeclarations = this.typeDeclarations()
			if (typeDeclarations && isLoaded) {
				const fileName = `/__lowgileLibs.d.ts`
				monaco.typescript.typescriptDefaults.addExtraLib(
					typeDeclarations,
					monaco.Uri.file(fileName).toString(),
				)
				if (!Object.hasOwn(globalThis, 'declarations')) {
					Object.defineProperty(globalThis, 'declarations', {
						get() {
							return monaco.typescript.typescriptDefaults.getExtraLibs()?.[
								`file://${fileName}`
							]?.content
						},
					})
				}
				// console.log(
				// 	monaco.languages.typescript.typescriptDefaults.getExtraLibs(),
				// )
			}
		})

		effect(() => {
			const editor = this.editor()
			const context = this.context()
			if (editor && context) {
				const code = this.code()
				const codeReadFromEditor = this.codeReadFromEditor()
				const contextKnownToEditor = this.contextKnownToEditor()
				if (codeReadFromEditor !== code || contextKnownToEditor !== context) {
					const newValue = this.addPrePostfixToCode(code, context)
					editor.setValue(newValue)
					this.contextKnownToEditor.set(context)
				}
				this.setHiddenAreas()
			}
		})
	}

	private editor = signal<Monaco.editor.IStandaloneCodeEditor | undefined>(
		undefined,
	)
	// @ViewChild('container', { static: true }) _editorContainer: ElementRef
	editorContainerRef = viewChild('container', { read: ElementRef })

	private async initMonaco() {
		await this.staticInit()

		const context = this.context()

		const editorOptions: Monaco.editor.IStandaloneEditorConstructionOptions = {
			...this.getDefaultEditorOptions(),
			theme: 'vs-dark', //this.context.theme, // this is currently not working as there can only be one theme in use. Using CSS class code-editor--invert instead
			language: languageAliases[context.codeLanguage],
			automaticLayout: true,
			model: monaco.editor.createModel(
				'',
				languageAliases[context.codeLanguage],
				monaco.Uri.file(
					`/code_${++CodeEditorComponent.counter}.${context.codeLanguage}`,
				),
			),
		}

		const editor = monaco.editor.create(
			this.editorContainerRef()?.nativeElement,
			editorOptions,
		)
		this.editor.set(editor)
		editor.onDidChangeModelContent((_event) => {
			const value = editor.getValue()
			const code = this.getCodeWithoutPrePostfix(value, context)
			this.codeReadFromEditor.set(code)
			this.code.set(code)
			this.onChange(code)
		})
		editor.onDidBlurEditorText(() => this.blur.emit())
		editor.onDidChangeCursorSelection(() => {
			const selections =
				editor
					.getSelections()
					?.map((sel) => editor.getModel().getValueInRange(sel)) ?? []
			this.selectionsChange.emit(selections)
		})

		setUpWrappers(editor, context, monaco, !DontProtectWrappers)
		this.setHiddenAreas()
	}

	addPrePostfixToCode(code: string, context: CodeContext) {
		return (
			(context?.prefix != null ? `${context.prefix}\n` : '') +
			code +
			(context?.postfix != null ? `\n${context.postfix}` : '')
		)
	}

	getCodeWithoutPrePostfix(code: string, context: CodeContext) {
		if (context.prefix == null && context.postfix == null) return code

		let lines = code.split('\n')
		lines = lines.slice(
			context.prefix != null ? context.prefix.split('\n').length : 0,
			lines.length -
				(context.postfix != null ? context.postfix.split('\n').length : 0),
		)

		return lines.join('\n')
	}

	getDefaultEditorOptions(): Monaco.editor.IStandaloneEditorConstructionOptions {
		const defaultOptions: Monaco.editor.IStandaloneEditorConstructionOptions = {
			minimap: { enabled: true },
			autoDetectHighContrast: false,
			fixedOverflowWidgets: true,
		}

		if (this.size() == 'singleline') {
			return {
				...defaultOptions,
				lineNumbers: 'off',
				glyphMargin: false,
				folding: false,
				scrollbar: {
					vertical: 'hidden',
					horizontal: 'auto',
				},
				minimap: {
					enabled: false,
				},
				scrollBeyondLastLine: false,
				overviewRulerLanes: 0,
				overviewRulerBorder: false,
				hideCursorInOverviewRuler: true,
			}
		}

		return {
			...defaultOptions,
			lineNumbers: (num) => `${num - this.context().prefixHiddenLineCount}`,
		}
	}

	ngOnInit() {}

	ngOnDestroy() {
		if (this.isFullscreen()) {
			this.closeFullscreen()
		}
		this.editor()?.dispose()
	}

	private staticInit() {
		if (!CodeEditorComponent.staticInitPromise) {
			CodeEditorComponent.staticInitPromise = (async () => {
				monaco.typescript.typescriptDefaults.setCompilerOptions({
					target: monaco.typescript.ScriptTarget.ESNext,
					allowNonTsExtensions: true,
					moduleResolution: monaco.typescript.ModuleResolutionKind.NodeJs,
					module: monaco.typescript.ModuleKind.CommonJS,
					typeRoots: ['node_modules/@types'],
					esModuleInterop: true,
				})

				this.declarationService.staticTypeDeclarations$.subscribe(
					(declarations) => {
						for (const [path, decl] of Object.entries(declarations)) {
							// monaco.languages.typescript.typescriptDefaults.addExtraLib(decl, Uri.file(`/node_modules/${path}`))
							monaco.typescript.typescriptDefaults.addExtraLib(
								decl as string,
								monaco.Uri.file(`/${path}`).toString(),
							)
						}
					},
				)

				// monacoEnvironment.getWorker = (_file: string, label: string) => {
				//   let workerUrl = monacoEnvironment.getWorkerUrl('workerMain.js', label)
				//   if(label == 'typescript') {
				//     // return new Worker(workerUrl, { name: label, type: 'module' })
				//     workerUrl = workerUrl.replace(/\bts\.worker\b/, 'ts-shared.worker')
				//     const sharedWorker = new SharedWorker(workerUrl, { name: label, type: 'module' })
				//     console.log('workerUrl', workerUrl)
				//     return {
				//       postMessage: sharedWorker.port.postMessage.bind(sharedWorker.port),
				//       set onmessage(fn) { sharedWorker.port.onmessage = fn },
				//       addEventListener: sharedWorker.port.addEventListener.bind(sharedWorker.port),
				//       removeEventListener: sharedWorker.port.removeEventListener.bind(sharedWorker.port),
				//       dispatchEvent: sharedWorker.port.dispatchEvent.bind(sharedWorker.port),
				//       set onmessageerror(fn) { sharedWorker.port.onmessageerror = fn },
				//     }
				//   }
				//   {
				//     // default functionality taken from https://github.com/microsoft/vscode/blob/151ef3514e76629f4e7bf3951439b1e0dae0a6e5/src/vs/base/browser/defaultWorkerFactory.ts
				//     const ttPolicy = null // doesn't seem to matter
				//     return new Worker(ttPolicy ? ttPolicy.createScriptURL(workerUrl) as unknown as string : workerUrl, { name: label, type: 'module' })
				//   }
				// }
			})()
		}
		return CodeEditorComponent.staticInitPromise
	}

	// TODO: Remove this once PropertiesComponent is migrated to used signal forms instead of form groups
	writeValue(value: string): void {
		this.code.set(value ?? '')
	}
	registerOnChange(onChange: (value: TsCodeType) => void): void {
		this.onChange = onChange
	}
	registerOnTouched(onTouched: () => void): void {
		this.onTouched = onTouched
	}
	setDisabledState?(_isDisabled: boolean): void {
		// TODO
	}

	setHiddenAreas() {
		const context = this.context()
		const editor = this.editor()
		if (editor) {
			if (this.devService.codeEditorShowHiddenAreas() || DontProtectWrappers) {
				;(editor as any).setHiddenAreas([])
				return
			}

			const model = editor.getModel() as Monaco.editor.ITextModel
			if (model) {
				const lastLine = model.getLineCount()

				const hiddenAreas: any[] = []
				if (context.prefixHiddenLineCount) {
					hiddenAreas.push({
						startLineNumber: 1,
						endLineNumber: context.prefixHiddenLineCount,
					})
				}
				if (context.postfixHiddenLineCount) {
					hiddenAreas.push({
						startLineNumber: lastLine + context.postfixHiddenLineCount - 1,
						endLineNumber: lastLine,
					})
				}

				;(editor as any).setHiddenAreas([]) // not part of public API
				;(editor as any).setHiddenAreas(hiddenAreas) // not part of public API
				return
			}
		}
	}
}
